/**
 * ReportItem - Sidebar item for a report
 *
 * Displays a report in the sidebar with its icon, name, and status.
 * Supports multi-select with Cmd/Ctrl+click and Shift+click.
 * Uses the unified SidebarItem component for consistent behavior.
 */

import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaFilePdf,
  FaClock,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaChartBar,
  FaCube,
  FaFileAlt,
} from "react-icons/fa";
import { Report, ReportStatus, ReportType } from "@/api/report";
import { createReportDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

// Status icon mapping
const STATUS_ICONS: Record<ReportStatus, React.ReactNode> = {
  pending: <FaClock size={6} />,
  generating: <FaSpinner className="animate-spin" size={6} />,
  generated: <FaCheck size={6} />,
  failed: <FaExclamationTriangle size={6} />,
};

// Status color mapping
const STATUS_COLORS: Record<ReportStatus, string> = {
  pending: "text-content-tertiary",
  generating: "text-info",
  generated: "text-success",
  failed: "text-error",
};

// Type icon mapping
const TYPE_ICONS: Record<ReportType, React.ReactNode> = {
  dashboard: <FaChartBar size={12} />,
  widget: <FaCube size={12} />,
  text: <FaFileAlt size={12} />,
};

interface ReportItemProps {
  report: Report;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all report IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

const ReportItem: React.FC<ReportItemProps> = ({
  report,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const displayName = report.name || "Sans nom";

  // Create drag payload
  const dragPayload = createReportDragPayload(report.id, displayName);

  const handleClick = useCallback(() => {
    navigateTo(`/reports?id=${report.id}`, { title: displayName });
  }, [report.id, displayName, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Status indicator
  const statusIndicator = (
    <span className={STATUS_COLORS[report.status]} title={report.status}>
      {STATUS_ICONS[report.status]}
    </span>
  );

  // Icon with type indicator
  const icon = (
    <div className="flex items-center gap-1">
      <FaFilePdf size={12} className="text-accent" />
    </div>
  );

  return (
    <>
      <SidebarItem
        id={report.id}
        name={displayName}
        resourceType="report"
        icon={icon}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        dragPayload={dragPayload}
        statusIndicator={statusIndicator}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

ReportItem.displayName = "ReportItem";

export default ReportItem;
